import { Input } from "@material-ui/core";
import {
  SaveAltOutlined,
  ArrowBack,
  RefreshOutlined,
  Business,
  Assessment,
  Add,
  DeleteOutline,
  Close,
  EditOutlined,
  Check,
  FileCopy as ContentCopyIcon,
  PeopleOutline,
  StorageOutlined,
  BarChart,
} from "@material-ui/icons";
import { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import fetchClient from "../../../api/fetchClient";
import Loader from "../../../components/Loader";
import message from "../../../helper/message";

const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
const CURRENT_YEAR = new Date().getFullYear();
const ANOS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);
const EMPTY_ENTRY = { empresaId: "", empresaNome: "", ano: CURRENT_YEAR, mes: new Date().getMonth() + 1, expense: "", cash: "" };

export default function AdminConcurControlApplication() {
  const history = useHistory();
  const [concurs, setConcurs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [maxValues, setMaxValues] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [activeTab, setActiveTab] = useState("controle");

  // Volumetria state
  const [selectedConcur, setSelectedConcur] = useState(null);
  const [aplicacoes, setAplicacoes] = useState([]);
  const [filterAno, setFilterAno] = useState(CURRENT_YEAR);
  const [filterMes, setFilterMes] = useState(new Date().getMonth() + 1);
  const [addForm, setAddForm] = useState(null);
  const [editingIdx, setEditingIdx] = useState(null);
  const [savingVol, setSavingVol] = useState(false);

  const getConcurs = async () => {
    try {
      setIsLoading(true);
      const { data } = await fetchClient.get("getAllConcur");
      setConcurs(data.value);
      const values = {};
      data.value.forEach((item) => { values[item.id] = item.maxEmpresas ?? ""; });
      setMaxValues(values);
    } catch (error) {
      message.error("Erro ao carregar aplicações Concur");
    } finally {
      setIsLoading(false);
    }
  };

  const getAplicacoes = async () => {
    try {
      let allData = [], nextToken = null;
      do {
        const { data } = await fetchClient.get(`getAllAplicacao?limit=100${nextToken ? `&nextToken=${nextToken}` : ""}`);
        allData = [...allData, ...data.value];
        nextToken = data.nextToken;
      } while (nextToken);
      setAplicacoes(allData.map((a) => ({ value: a.id, label: a.description, database: a.database })));
    } catch (error) {
      console.error("Erro ao carregar aplicações:", error);
    }
  };

  useEffect(() => { getConcurs(); getAplicacoes(); }, []); // eslint-disable-line

  // ── Summary KPIs ───────────────────────────────────────────
  const summaryKpis = useMemo(() => {
    const totalBases = concurs.reduce((a, c) => a + (c.empresa?.length || 0), 0);
    const totalVolEntries = concurs.reduce((a, c) => a + (c["volumetria-real"]?.length || 0), 0);
    return { totalConcurs: concurs.length, totalBases, totalVolEntries };
  }, [concurs]);

  // ── Max empresas ───────────────────────────────────────────
  const handleMaxChange = (id, value) => setMaxValues((prev) => ({ ...prev, [id]: value }));

  const handleSaveMaxEmpresas = async (concurId) => {
    const maxEmpresas = Number(maxValues[concurId]);
    if (!maxEmpresas || isNaN(maxEmpresas) || maxEmpresas <= 0) { message.error("Máximo de empresas inválido"); return; }
    const concur = concurs.find((c) => c.id === concurId);
    if (!concur) { message.error("Concur não encontrado"); return; }
    try {
      setSavingId(concurId);
      await fetchClient.post("createConcur", { ...concur, maxEmpresas });
      message.sucess("Atualizado com sucesso!");
      await getConcurs();
    } catch { message.error("Erro ao atualizar"); }
    finally { setSavingId(null); }
  };

  // ── Volumetria helpers ─────────────────────────────────────
  const openVolumetria = (concur) => {
    setSelectedConcur(concur);
    setActiveTab("volumetria");
    setAddForm(null);
    setEditingIdx(null);
  };

  const basesOptions = selectedConcur
    ? (selectedConcur.empresa || []).map((e) => {
        const app = aplicacoes.find((a) => a.value === e.empresa);
        return { value: e.empresa, label: app ? (app.database || app.label) : e.empresa };
      })
    : [];

  const volEntries = selectedConcur?.["volumetria-real"] || [];
  const filteredEntries = volEntries.filter(
    (e) => Number(e.ano) === Number(filterAno) && Number(e.mes) === Number(filterMes)
  );

  const saveVolumetria = async (newEntries) => {
    const updated = { ...selectedConcur, "volumetria-real": newEntries };
    setSavingVol(true);
    try {
      await fetchClient.post("createConcur", updated);
      message.sucess("Volumetria salva!");
      setConcurs((prev) => prev.map((c) => c.id === updated.id ? updated : c));
      setSelectedConcur(updated);
    } catch { message.error("Erro ao salvar volumetria"); }
    finally { setSavingVol(false); }
  };

  const handleAddEntry = async () => {
    if (!addForm.empresaId) { message.error("Selecione uma base"); return; }
    if (addForm.expense === "" && addForm.cash === "") { message.error("Informe ao menos um valor"); return; }
    const existing = volEntries.findIndex(
      (e) => e.empresaId === addForm.empresaId && Number(e.ano) === Number(addForm.ano) && Number(e.mes) === Number(addForm.mes)
    );
    const app = aplicacoes.find((a) => a.value === addForm.empresaId);
    const entry = { ...addForm, empresaNome: app?.database || app?.label || addForm.empresaId, expense: Number(addForm.expense) || 0, cash: Number(addForm.cash) || 0 };
    const newEntries = existing >= 0 ? volEntries.map((e, i) => i === existing ? entry : e) : [...volEntries, entry];
    await saveVolumetria(newEntries);
    setAddForm(null);
  };

  const handleCopyToAllMonths = async () => {
    if (filteredEntries.length === 0) { message.error("Nenhuma entrada para copiar"); return; }
    if (!window.confirm(`Copiar ${filteredEntries.length} entrada(s) de ${MESES[filterMes - 1]}/${filterAno} para todos os meses de ${filterAno}?`)) return;
    const outsideYear = volEntries.filter((e) => Number(e.ano) !== Number(filterAno));
    const insideOther = volEntries.filter((e) => Number(e.ano) === Number(filterAno) && Number(e.mes) !== Number(filterMes));
    const copied = [];
    Array.from({ length: 12 }, (_, i) => i + 1).forEach((mes) => {
      if (mes === filterMes) return;
      filteredEntries.forEach((entry) => copied.push({ ...entry, mes, ano: filterAno }));
    });
    const merged = insideOther.filter((ex) => !copied.some((c) => c.empresaId === ex.empresaId && Number(c.mes) === Number(ex.mes)));
    await saveVolumetria([...outsideYear, ...filteredEntries, ...merged, ...copied]);
    message.sucess(`Copiado para todos os meses de ${filterAno}!`);
  };

  const handleDeleteEntry = async (idx) => {
    if (!window.confirm("Remover esta entrada?")) return;
    const allIdx = volEntries.indexOf(filteredEntries[idx]);
    await saveVolumetria(volEntries.filter((_, i) => i !== allIdx));
  };

  const handleDeleteAllYear = async () => {
    const yearEntries = volEntries.filter((e) => Number(e.ano) === Number(filterAno));
    if (yearEntries.length === 0) { message.error(`Nenhuma entrada para ${filterAno}`); return; }
    if (!window.confirm(`Apagar todos os ${yearEntries.length} registros de volumetria de ${filterAno}?`)) return;
    await saveVolumetria(volEntries.filter((e) => Number(e.ano) !== Number(filterAno)));
    message.sucess(`Todos os registros de ${filterAno} foram removidos!`);
  };

  const handleSaveInlineEdit = async (idx) => {
    const allIdx = volEntries.indexOf(filteredEntries[idx]);
    const app = aplicacoes.find((a) => a.value === editingIdx.empresaId);
    const updated = { ...editingIdx, empresaNome: app?.database || app?.label || editingIdx.empresaId, expense: Number(editingIdx.expense) || 0, cash: Number(editingIdx.cash) || 0 };
    await saveVolumetria(volEntries.map((e, i) => i === allIdx ? updated : e));
    setEditingIdx(null);
  };

  const TABS = [
    { id: "controle",   label: "Controle de Acessos", icon: <PeopleOutline style={{ fontSize: 18 }} /> },
    { id: "volumetria", label: "Volumetria",           icon: <BarChart style={{ fontSize: 18 }} /> },
  ];

  return (
    <Page>
      {/* Header */}
      <Header>
        <HeaderLeft>
          <BackBtn onClick={() => history.goBack()}><ArrowBack /></BackBtn>
          <div>
            <HeaderTitle><Business style={{ marginRight: 10, fontSize: 28 }} />Controle de Concur</HeaderTitle>
            <HeaderSub>Gerencie limites de acesso e volumetria por cadastro</HeaderSub>
          </div>
        </HeaderLeft>
        <RefreshBtn onClick={getConcurs} disabled={isLoading}>
          <RefreshOutlined style={{ fontSize: 18 }} />
          {isLoading ? "Atualizando..." : "Atualizar"}
        </RefreshBtn>
      </Header>

      {/* KPI Summary */}
      {!isLoading && (
        <KpiRow>
          <KpiCard color="#667eea">
            <KpiIcon color="#667eea"><Business style={{ fontSize: 22 }} /></KpiIcon>
            <KpiContent>
              <KpiValue>{summaryKpis.totalConcurs}</KpiValue>
              <KpiLabel>Cadastros Concur</KpiLabel>
            </KpiContent>
          </KpiCard>
          <KpiCard color="#27ae60">
            <KpiIcon color="#27ae60"><StorageOutlined style={{ fontSize: 22 }} /></KpiIcon>
            <KpiContent>
              <KpiValue>{summaryKpis.totalBases}</KpiValue>
              <KpiLabel>Bases Vinculadas</KpiLabel>
            </KpiContent>
          </KpiCard>
          <KpiCard color="#e67e22">
            <KpiIcon color="#e67e22"><Assessment style={{ fontSize: 22 }} /></KpiIcon>
            <KpiContent>
              <KpiValue>{summaryKpis.totalVolEntries}</KpiValue>
              <KpiLabel>Entradas de Volumetria</KpiLabel>
            </KpiContent>
          </KpiCard>
        </KpiRow>
      )}

      {/* Tab navigation */}
      <TabBar>
        {TABS.map((t) => (
          <TabBtn key={t.id} active={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
            {t.icon} {t.label}
          </TabBtn>
        ))}
      </TabBar>

      {/* ── Tab: Controle ─────────────────────────────────────── */}
      {activeTab === "controle" && (
        <Card>
          {isLoading ? (
            <LoaderWrap><Loader /></LoaderWrap>
          ) : concurs.length === 0 ? (
            <Empty>
              <Business style={{ fontSize: 48, color: "#9CA3AF" }} />
              <EmptyTitle>Nenhum Concur cadastrado</EmptyTitle>
            </Empty>
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th align="left">Concur</Th>
                    <Th>Bases vinculadas</Th>
                    <Th>Máx. empresas</Th>
                    <Th>Volumetria</Th>
                    <Th>Ações</Th>
                  </tr>
                </thead>
                <tbody>
                  {concurs.map((concur) => {
                    const volCount = concur["volumetria-real"]?.length || 0;
                    const bases = concur.empresa?.length || 0;
                    const max = concur.maxEmpresas || 0;
                    const pct = max > 0 ? Math.min((bases / max) * 100, 100) : 0;
                    return (
                      <TR key={concur.id}>
                        <Td align="left">
                          <ConcurName>{concur.name}</ConcurName>
                          <ConcurId title={concur.id}>{concur.id.slice(0, 8)}…</ConcurId>
                        </Td>
                        <Td>
                          <ProgressWrap>
                            <ProgressText><strong>{bases}</strong> / {max || "—"}</ProgressText>
                            {max > 0 && <ProgressBar><ProgressFill pct={pct} warn={pct >= 80} /></ProgressBar>}
                          </ProgressWrap>
                        </Td>
                        <Td>
                          <MaxInputStyled
                            type="number" min={1}
                            value={maxValues[concur.id] ?? ""}
                            onChange={(e) => handleMaxChange(concur.id, e.target.value)}
                          />
                        </Td>
                        <Td>
                          <VolChipBtn onClick={() => openVolumetria(concur)}>
                            <Assessment style={{ fontSize: 15 }} />
                            {volCount} entr.
                          </VolChipBtn>
                        </Td>
                        <Td>
                          <SaveRowBtn onClick={() => handleSaveMaxEmpresas(concur.id)} disabled={savingId === concur.id}>
                            <SaveAltOutlined style={{ fontSize: 16 }} />
                            {savingId === concur.id ? "Salvando…" : "Salvar"}
                          </SaveRowBtn>
                        </Td>
                      </TR>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </Card>
      )}

      {/* ── Tab: Volumetria ───────────────────────────────────── */}
      {activeTab === "volumetria" && (
        <VolTabLayout>
          {/* Left: concur list */}
          <ConcurList>
            <ConcurListTitle>Cadastros</ConcurListTitle>
            {isLoading ? <Loader /> : concurs.map((c) => (
              <ConcurListItem
                key={c.id}
                active={selectedConcur?.id === c.id}
                onClick={() => { setSelectedConcur(c); setAddForm(null); setEditingIdx(null); }}
              >
                <ConcurItemName>{c.name}</ConcurItemName>
                <ConcurItemMeta>
                  <span>{c.empresa?.length || 0} bases</span>
                  <VolPill>{c["volumetria-real"]?.length || 0} vol.</VolPill>
                </ConcurItemMeta>
              </ConcurListItem>
            ))}
          </ConcurList>

          {/* Right: volumetria panel */}
          <VolPanel>
            {!selectedConcur ? (
              <VolEmptySelect>
                <Assessment style={{ fontSize: 52, color: "#cbd5e1" }} />
                <VolEmptyTitle>Selecione um cadastro</VolEmptyTitle>
                <VolEmptyText>Escolha um Concur na lista ao lado para gerenciar sua volumetria</VolEmptyText>
              </VolEmptySelect>
            ) : (
              <>
                <VolPanelTop>
                  <VolPanelTitle>
                    <Assessment style={{ fontSize: 20, marginRight: 8, color: "#667eea" }} />
                    <span>{selectedConcur.name}</span>
                  </VolPanelTitle>
                  <VolPanelControls>
                    <PeriodSel value={filterAno} onChange={(e) => setFilterAno(Number(e.target.value))}>
                      {ANOS.map((a) => <option key={a} value={a}>{a}</option>)}
                    </PeriodSel>
                    <PeriodSel value={filterMes} onChange={(e) => setFilterMes(Number(e.target.value))}>
                      {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                    </PeriodSel>
                    <AddBtn onClick={() => setAddForm({ ...EMPTY_ENTRY, ano: filterAno, mes: filterMes })}>
                      <Add style={{ fontSize: 17 }} /> Adicionar
                    </AddBtn>
                    <CopyBtn
                      onClick={handleCopyToAllMonths}
                      disabled={filteredEntries.length === 0 || savingVol}
                      title="Copiar para todos os meses do ano"
                    >
                      <ContentCopyIcon style={{ fontSize: 16 }} /> Copiar p/ todos os meses
                    </CopyBtn>
                    <DeleteYearBtn
                      onClick={handleDeleteAllYear}
                      disabled={savingVol}
                      title={`Apagar todos os registros de ${filterAno}`}
                    >
                      <DeleteOutline style={{ fontSize: 16 }} /> Apagar ano {filterAno}
                    </DeleteYearBtn>
                  </VolPanelControls>
                </VolPanelTop>

                {/* Year summary chips */}
                <MonthChips>
                  {MESES.map((m, i) => {
                    const mes = i + 1;
                    const count = volEntries.filter(e => Number(e.ano) === filterAno && Number(e.mes) === mes).length;
                    return (
                      <MonthChip
                        key={mes}
                        active={filterMes === mes}
                        hasData={count > 0}
                        onClick={() => setFilterMes(mes)}
                      >
                        {m}
                        {count > 0 && <MonthChipDot />}
                      </MonthChip>
                    );
                  })}
                </MonthChips>

                {/* Add form */}
                {addForm && (
                  <AddFormCard>
                    <AddFormTitle>Nova entrada — {MESES[filterMes - 1]}/{filterAno}</AddFormTitle>
                    <AddFormGrid>
                      <FormField>
                        <FormLabel>Base</FormLabel>
                        <FormSel value={addForm.empresaId} onChange={(e) => setAddForm({ ...addForm, empresaId: e.target.value })}>
                          <option value="">Selecionar…</option>
                          {basesOptions.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                        </FormSel>
                      </FormField>
                      <FormField>
                        <FormLabel>Ano</FormLabel>
                        <FormSel value={addForm.ano} onChange={(e) => setAddForm({ ...addForm, ano: Number(e.target.value) })}>
                          {ANOS.map((a) => <option key={a} value={a}>{a}</option>)}
                        </FormSel>
                      </FormField>
                      <FormField>
                        <FormLabel>Mês</FormLabel>
                        <FormSel value={addForm.mes} onChange={(e) => setAddForm({ ...addForm, mes: Number(e.target.value) })}>
                          {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                        </FormSel>
                      </FormField>
                      <FormField>
                        <FormLabel>Expense</FormLabel>
                        <FormInput type="number" placeholder="Ex: 1000" value={addForm.expense} onChange={(e) => setAddForm({ ...addForm, expense: e.target.value })} />
                      </FormField>
                      <FormField>
                        <FormLabel>Cash</FormLabel>
                        <FormInput type="number" placeholder="Ex: 500" value={addForm.cash} onChange={(e) => setAddForm({ ...addForm, cash: e.target.value })} />
                      </FormField>
                      <FormField style={{ flexDirection: "row", alignItems: "flex-end", gap: 8 }}>
                        <CancelFormBtn onClick={() => setAddForm(null)}>Cancelar</CancelFormBtn>
                        <ConfirmFormBtn onClick={handleAddEntry} disabled={savingVol}>
                          <Check style={{ fontSize: 16 }} /> {savingVol ? "…" : "Confirmar"}
                        </ConfirmFormBtn>
                      </FormField>
                    </AddFormGrid>
                  </AddFormCard>
                )}

                {/* Entries table */}
                {filteredEntries.length === 0 ? (
                  <VolEmpty>
                    <Assessment style={{ fontSize: 36, color: "#cbd5e1" }} />
                    <span>Nenhuma entrada para {MESES[filterMes - 1]}/{filterAno}</span>
                  </VolEmpty>
                ) : (
                  <>
                    <VolTable>
                      <thead>
                        <tr>
                          <VTh align="left">Base</VTh>
                          <VTh>Expense</VTh>
                          <VTh>Cash</VTh>
                          <VTh>Total</VTh>
                          <VTh>Ações</VTh>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEntries.map((entry, idx) => {
                          const isEdit = editingIdx?._idx === idx;
                          return (
                            <VTR key={idx}>
                              <VTd align="left">
                                {isEdit ? (
                                  <FormSel value={editingIdx.empresaId} onChange={(e) => setEditingIdx({ ...editingIdx, empresaId: e.target.value })} style={{ minWidth: 200 }}>
                                    {basesOptions.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                                  </FormSel>
                                ) : <BaseTag>{entry.empresaNome || entry.empresaId}</BaseTag>}
                              </VTd>
                              <VTd>
                                {isEdit
                                  ? <InlineInput type="number" value={editingIdx.expense} onChange={(e) => setEditingIdx({ ...editingIdx, expense: e.target.value })} />
                                  : <ExpVal>{(entry.expense || 0).toLocaleString("pt-BR")}</ExpVal>}
                              </VTd>
                              <VTd>
                                {isEdit
                                  ? <InlineInput type="number" value={editingIdx.cash} onChange={(e) => setEditingIdx({ ...editingIdx, cash: e.target.value })} />
                                  : <CashVal>{(entry.cash || 0).toLocaleString("pt-BR")}</CashVal>}
                              </VTd>
                              <VTd><TotalVal>{((entry.expense || 0) + (entry.cash || 0)).toLocaleString("pt-BR")}</TotalVal></VTd>
                              <VTd>
                                <Actions>
                                  {isEdit ? (
                                    <>
                                      <ActionBtn variant="save" onClick={() => handleSaveInlineEdit(idx)} disabled={savingVol}><Check style={{ fontSize: 15 }} /></ActionBtn>
                                      <ActionBtn variant="cancel" onClick={() => setEditingIdx(null)}><Close style={{ fontSize: 15 }} /></ActionBtn>
                                    </>
                                  ) : (
                                    <>
                                      <ActionBtn variant="edit" onClick={() => setEditingIdx({ ...entry, _idx: idx })}><EditOutlined style={{ fontSize: 15 }} /></ActionBtn>
                                      <ActionBtn variant="delete" onClick={() => handleDeleteEntry(idx)}><DeleteOutline style={{ fontSize: 15 }} /></ActionBtn>
                                    </>
                                  )}
                                </Actions>
                              </VTd>
                            </VTR>
                          );
                        })}
                      </tbody>
                    </VolTable>

                    {/* Summary */}
                    <VolSummary>
                      {[
                        { label: "Total Expense", value: filteredEntries.reduce((a, e) => a + (e.expense || 0), 0), color: "#27ae60" },
                        { label: "Total Cash",    value: filteredEntries.reduce((a, e) => a + (e.cash    || 0), 0), color: "#e67e22" },
                        { label: "Total Geral",   value: filteredEntries.reduce((a, e) => a + (e.expense || 0) + (e.cash || 0), 0), color: "#667eea" },
                        { label: "Bases",         value: filteredEntries.length, color: "#8e44ad" },
                      ].map(({ label, value, color }) => (
                        <SumCard key={label}>
                          <SumLabel>{label}</SumLabel>
                          <SumValue color={color}>{value.toLocaleString("pt-BR")}</SumValue>
                        </SumCard>
                      ))}
                    </VolSummary>
                  </>
                )}
              </>
            )}
          </VolPanel>
        </VolTabLayout>
      )}
    </Page>
  );
}

// ── Animations ────────────────────────────────────────────────
const fadeIn = keyframes`from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); }`;

// ── Layout ────────────────────────────────────────────────────
const Page = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 28px 24px 48px;
  font-family: 'Mulish', sans-serif;
  box-sizing: border-box;
`;

const Header = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px 28px;
  margin-bottom: 20px;
  box-shadow: 0 4px 16px rgba(15,23,42,0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const HeaderLeft = styled.div`display: flex; align-items: center; gap: 16px;`;

const BackBtn = styled.button`
  width: 40px; height: 40px;
  border-radius: 12px; border: none;
  background: rgba(102,126,234,0.1); color: #667eea;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.2s;
  &:hover { background: rgba(102,126,234,0.2); transform: translateX(-2px); }
`;

const HeaderTitle = styled.h1`
  font-size: 24px; font-weight: 800; color: #1f2933; margin: 0 0 4px;
  display: flex; align-items: center; font-family: 'Mulish', sans-serif;
`;

const HeaderSub = styled.p`
  font-size: 14px; color: #6b7280; margin: 0; font-family: 'Mulish', sans-serif;
`;

const RefreshBtn = styled.button`
  border: none; border-radius: 12px; padding: 10px 18px;
  font-weight: 700; font-family: 'Mulish', sans-serif; font-size: 14px;
  cursor: pointer; display: inline-flex; align-items: center; gap: 8px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white;
  box-shadow: 0 4px 12px rgba(102,126,234,0.3); transition: all 0.2s;
  &:disabled { opacity: 0.7; cursor: not-allowed; }
  &:hover:not(:disabled) { transform: translateY(-1px); }
`;

// ── KPI Cards ─────────────────────────────────────────────────
const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
  animation: ${fadeIn} 0.4s ease;
`;

const KpiCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 18px 20px;
  border-left: 4px solid ${p => p.color};
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  display: flex; align-items: center; gap: 14px;
`;

const KpiIcon = styled.div`
  width: 44px; height: 44px;
  border-radius: 12px;
  background: ${p => p.color}18;
  color: ${p => p.color};
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
`;

const KpiContent = styled.div``;

const KpiValue = styled.div`
  font-size: 28px; font-weight: 800; color: #1f2933; font-family: 'Mulish', sans-serif;
`;

const KpiLabel = styled.div`
  font-size: 12px; font-weight: 600; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.05em; font-family: 'Mulish', sans-serif;
`;

// ── Tabs ──────────────────────────────────────────────────────
const TabBar = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #e2e8f0;
  margin-bottom: 20px;
`;

const TabBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 20px;
  border: none; background: none; cursor: pointer;
  font-size: 14px; font-weight: 700; font-family: 'Mulish', sans-serif;
  color: ${p => p.active ? '#667eea' : '#64748b'};
  border-bottom: 3px solid ${p => p.active ? '#667eea' : 'transparent'};
  margin-bottom: -2px;
  transition: all 0.2s;
  border-radius: 8px 8px 0 0;
  &:hover { color: #667eea; background: rgba(102,126,234,0.05); }
`;

// ── Card ──────────────────────────────────────────────────────
const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(15,23,42,0.07);
  animation: ${fadeIn} 0.3s ease;
`;

const LoaderWrap = styled.div`
  min-height: 240px; display: flex; align-items: center; justify-content: center;
`;

const Empty = styled.div`
  padding: 60px 24px; display: flex; flex-direction: column;
  align-items: center; gap: 12px; color: #94a3b8; font-family: 'Mulish', sans-serif;
`;

const EmptyTitle = styled.h3`font-size: 18px; font-weight: 600; margin: 0; color: #4b5563;`;

// ── Controle Table ────────────────────────────────────────────
const TableWrap = styled.div`overflow-x: auto;`;

const Table = styled.table`width: 100%; border-collapse: collapse; min-width: 600px;`;

const Th = styled.th`
  text-align: ${p => p.align || 'center'};
  padding: 12px 16px; font-size: 11px; font-weight: 700;
  color: #6b7280; text-transform: uppercase; letter-spacing: 0.07em;
  border-bottom: 2px solid #f0f4fa; font-family: 'Mulish', sans-serif;
`;

const TR = styled.tr`
  border-bottom: 1px solid #f0f4fa; transition: background 0.15s;
  &:hover { background: #f9fbff; }
`;

const Td = styled.td`
  padding: 14px 16px; text-align: ${p => p.align || 'center'};
  font-size: 14px; color: #1f2933; font-family: 'Mulish', sans-serif;
`;

const ConcurName = styled.div`font-weight: 700; font-size: 15px; color: #1f2933;`;

const ConcurId = styled.div`
  font-size: 11px; color: #94a3b8; font-family: monospace; margin-top: 2px;
`;

const ProgressWrap = styled.div`display: flex; flex-direction: column; gap: 4px; align-items: center;`;

const ProgressText = styled.div`font-size: 13px; font-weight: 600; color: #4b5563;`;

const ProgressBar = styled.div`
  width: 80px; height: 5px; background: #f0f0f0; border-radius: 99px; overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${p => p.pct}%;
  background: ${p => p.warn ? '#e67e22' : '#27ae60'};
  border-radius: 99px;
`;

const MaxInputStyled = styled(Input)`
  width: 80px;
  .MuiInputBase-input { text-align: center; padding: 6px 8px; }
`;

const VolChipBtn = styled.button`
  display: inline-flex; align-items: center; gap: 5px;
  border: 1.5px solid #e0e7ff; border-radius: 20px;
  padding: 5px 12px; font-size: 13px; font-weight: 700;
  font-family: 'Mulish', sans-serif;
  background: rgba(102,126,234,0.06); color: #667eea;
  cursor: pointer; transition: all 0.2s;
  &:hover { background: rgba(102,126,234,0.14); border-color: #667eea; }
`;

const SaveRowBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  border: none; border-radius: 10px; padding: 8px 14px;
  font-size: 13px; font-weight: 700; font-family: 'Mulish', sans-serif;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white;
  cursor: pointer; transition: all 0.2s; box-shadow: 0 2px 8px rgba(102,126,234,0.3);
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { transform: translateY(-1px); }
`;

// ── Volumetria Tab Layout ─────────────────────────────────────
const VolTabLayout = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 20px;
  align-items: start;
  animation: ${fadeIn} 0.3s ease;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

// Left panel – concur list
const ConcurList = styled.div`
  background: white;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 16px rgba(15,23,42,0.07);
  display: flex; flex-direction: column; gap: 6px;
`;

const ConcurListTitle = styled.div`
  font-size: 11px; font-weight: 700; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.08em;
  font-family: 'Mulish', sans-serif;
  padding: 4px 8px 12px;
  border-bottom: 1px solid #f0f4fa;
  margin-bottom: 4px;
`;

const ConcurListItem = styled.button`
  width: 100%; text-align: left; border: none;
  border-radius: 10px; padding: 10px 12px; cursor: pointer;
  font-family: 'Mulish', sans-serif;
  background: ${p => p.active ? 'rgba(102,126,234,0.1)' : 'transparent'};
  border: 1.5px solid ${p => p.active ? '#667eea' : 'transparent'};
  transition: all 0.15s;
  &:hover { background: rgba(102,126,234,0.07); }
`;

const ConcurItemName = styled.div`
  font-size: 14px; font-weight: 700; color: #1f2933;
`;

const ConcurItemMeta = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-top: 4px; font-size: 12px; color: #94a3b8;
`;

const VolPill = styled.span`
  background: rgba(102,126,234,0.1); color: #667eea;
  border-radius: 20px; padding: 1px 7px;
  font-size: 11px; font-weight: 700;
`;

// Right panel – volumetria content
const VolPanel = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(15,23,42,0.07);
  min-height: 400px;
`;

const VolEmptySelect = styled.div`
  height: 360px; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 12px;
`;

const VolEmptyTitle = styled.h3`font-size: 17px; font-weight: 700; color: #4b5563; margin: 0;`;
const VolEmptyText = styled.p`font-size: 14px; color: #94a3b8; margin: 0; text-align: center; max-width: 300px;`;

const VolPanelTop = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; margin-bottom: 16px; flex-wrap: wrap;
`;

const VolPanelTitle = styled.h2`
  font-size: 17px; font-weight: 800; color: #1f2933; margin: 0;
  display: flex; align-items: center; font-family: 'Mulish', sans-serif;
  span { color: #667eea; margin-left: 4px; }
`;

const VolPanelControls = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap;`;

const PeriodSel = styled.select`
  border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 7px 10px;
  font-size: 13px; font-family: 'Mulish', sans-serif; color: #1f2933;
  background: white; outline: none;
  &:focus { border-color: #667eea; }
`;

const AddBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  border: none; border-radius: 8px; padding: 8px 14px;
  font-size: 13px; font-weight: 700; font-family: 'Mulish', sans-serif;
  background: linear-gradient(135deg, #27ae60 0%, #229954 100%); color: white;
  cursor: pointer; transition: all 0.2s; box-shadow: 0 3px 8px rgba(39,174,96,0.25);
  &:hover { transform: translateY(-1px); }
`;

const CopyBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  border: 1.5px solid #e67e22; border-radius: 8px; padding: 7px 14px;
  font-size: 13px; font-weight: 700; font-family: 'Mulish', sans-serif;
  background: white; color: #e67e22; cursor: pointer; transition: all 0.2s;
  &:hover:not(:disabled) { background: rgba(230,126,34,0.07); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const DeleteYearBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  border: 1.5px solid #e74c3c; border-radius: 8px; padding: 7px 14px;
  font-size: 13px; font-weight: 700; font-family: 'Mulish', sans-serif;
  background: white; color: #e74c3c; cursor: pointer; transition: all 0.2s;
  &:hover:not(:disabled) { background: rgba(231,76,60,0.07); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// Month chips
const MonthChips = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px;
  padding: 12px 0; border-bottom: 1px solid #f0f4fa; margin-bottom: 16px;
`;

const MonthChip = styled.button`
  position: relative;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1.5px solid ${p => p.active ? '#667eea' : p.hasData ? '#e0e7ff' : '#e2e8f0'};
  border-radius: 20px; padding: 4px 12px;
  font-size: 12px; font-weight: ${p => p.active ? '800' : '600'};
  font-family: 'Mulish', sans-serif;
  background: ${p => p.active ? 'rgba(102,126,234,0.12)' : p.hasData ? 'rgba(102,126,234,0.04)' : 'white'};
  color: ${p => p.active ? '#667eea' : p.hasData ? '#667eea' : '#94a3b8'};
  cursor: pointer; transition: all 0.15s;
  &:hover { border-color: #667eea; color: #667eea; }
`;

const MonthChipDot = styled.span`
  position: absolute; top: -2px; right: -2px;
  width: 7px; height: 7px;
  border-radius: 50%; background: #667eea;
  border: 1.5px solid white;
`;

// Add form
const AddFormCard = styled.div`
  background: #f8f9ff; border: 1.5px solid #e0e7ff;
  border-radius: 12px; padding: 16px 20px; margin-bottom: 16px;
  animation: ${fadeIn} 0.2s ease;
`;

const AddFormTitle = styled.div`
  font-size: 13px; font-weight: 700; color: #667eea;
  font-family: 'Mulish', sans-serif; margin-bottom: 14px;
`;

const AddFormGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 2fr) 90px 90px 110px 110px auto;
  gap: 10px; align-items: end;
  @media (max-width: 900px) { grid-template-columns: repeat(3, 1fr); }
`;

const FormField = styled.div`display: flex; flex-direction: column; gap: 5px;`;

const FormLabel = styled.label`
  font-size: 11px; font-weight: 700; color: #64748b;
  text-transform: uppercase; letter-spacing: 0.05em; font-family: 'Mulish', sans-serif;
`;

const FormSel = styled.select`
  border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 7px 10px;
  font-size: 13px; font-family: 'Mulish', sans-serif; color: #1f2933;
  background: white; outline: none;
  &:focus { border-color: #667eea; }
`;

const FormInput = styled.input`
  border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 7px 10px;
  font-size: 13px; font-family: 'Mulish', sans-serif; color: #1f2933;
  background: white; outline: none; width: 100%; box-sizing: border-box;
  &:focus { border-color: #667eea; }
`;

const CancelFormBtn = styled.button`
  border: 1.5px solid #e2e8f0; border-radius: 8px; padding: 7px 12px;
  font-size: 13px; font-family: 'Mulish', sans-serif;
  background: white; color: #64748b; cursor: pointer;
  &:hover { border-color: #94a3b8; }
`;

const ConfirmFormBtn = styled.button`
  display: inline-flex; align-items: center; gap: 5px;
  border: none; border-radius: 8px; padding: 7px 14px;
  font-size: 13px; font-weight: 700; font-family: 'Mulish', sans-serif;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// Volumetria table
const VolTable = styled.table`width: 100%; border-collapse: collapse;`;

const VTh = styled.th`
  text-align: ${p => p.align || 'center'};
  padding: 10px 14px; font-size: 11px; font-weight: 700;
  color: #6b7280; text-transform: uppercase; letter-spacing: 0.06em;
  border-bottom: 2px solid #f0f4fa; font-family: 'Mulish', sans-serif;
`;

const VTR = styled.tr`
  border-bottom: 1px solid #f8f9fa;
  &:hover { background: #fafbff; }
`;

const VTd = styled.td`
  padding: 11px 14px; text-align: ${p => p.align || 'center'};
  font-size: 14px; font-family: 'Mulish', sans-serif;
`;

const BaseTag = styled.span`
  font-family: monospace; font-size: 13px; font-weight: 600;
  color: #2c3e50; background: #f8f9fa; padding: 3px 8px; border-radius: 6px;
`;

const ExpVal = styled.span`font-weight: 700; color: #27ae60;`;
const CashVal = styled.span`font-weight: 700; color: #e67e22;`;
const TotalVal = styled.span`font-weight: 700; color: #667eea;`;

const Actions = styled.div`display: flex; gap: 6px; justify-content: center;`;

const ActionBtn = styled.button`
  width: 28px; height: 28px; border: none; border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.15s;
  ${p => p.variant === 'edit'   ? 'background:rgba(52,152,219,0.1);color:#2980b9;&:hover{background:#3498db;color:white;}' : ''}
  ${p => p.variant === 'delete' ? 'background:rgba(231,76,60,0.1);color:#e74c3c;&:hover{background:#e74c3c;color:white;}' : ''}
  ${p => p.variant === 'save'   ? 'background:rgba(39,174,96,0.1);color:#27ae60;&:hover{background:#27ae60;color:white;}' : ''}
  ${p => p.variant === 'cancel' ? 'background:rgba(100,116,139,0.1);color:#64748b;&:hover{background:#64748b;color:white;}' : ''}
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const InlineInput = styled.input`
  border: 1.5px solid #e2e8f0; border-radius: 6px; padding: 4px 8px;
  font-size: 13px; font-family: 'Mulish', sans-serif; width: 80px; outline: none;
  &:focus { border-color: #667eea; }
`;

const VolEmpty = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 40px 24px; color: #94a3b8; font-size: 14px; font-family: 'Mulish', sans-serif;
`;

const VolSummary = styled.div`
  display: flex; gap: 12px; margin-top: 16px;
  padding-top: 16px; border-top: 2px solid #f0f4fa; flex-wrap: wrap;
`;

const SumCard = styled.div`
  flex: 1; min-width: 100px;
  background: #f8f9fa; border-radius: 10px; padding: 12px 16px; text-align: center;
`;

const SumLabel = styled.div`
  font-size: 11px; font-weight: 600; color: #94a3b8;
  text-transform: uppercase; letter-spacing: 0.04em; font-family: 'Mulish', sans-serif; margin-bottom: 6px;
`;

const SumValue = styled.div`
  font-size: 20px; font-weight: 800; color: ${p => p.color || '#667eea'};
  font-family: 'Mulish', sans-serif;
`;
